/**
 * Adapter that loads Ops vehicles for a given network using the date-free
 * `getvehicles` master-list endpoint (W7).
 *
 * Vehicle loading is now strictly date-free: no reference date is accepted or
 * required.  The schedule/shift-scraping fallback that existed in earlier
 * iterations has been removed — callers receive vehicles directly from the
 * network's registered fleet master, not from dated shift schedules.
 *
 * The function signature and return type (`OpsVehicleLoadResult`) are stable
 * so callers in `OpsVehicleMappingPanel` and any tests need no changes beyond
 * removing the `referenceDate` argument.
 */

import type { Environment, FixedRouteVehicleListItem } from '../fixedRouteUatClient';
import { fetchFixedRouteVehicleList, fetchNetworkVehicleMaster } from '../fixedRouteUatClient';
import type { OpsVehicle, OpsVehicleLoadResult } from './opsVehicleTypes';

/**
 * Loads the date-free vehicle master list for a network from the Ops API.
 *
 * Calls the `getvehicles` endpoint and returns all vehicles registered to the
 * network.  No date parameter is required or accepted.
 *
 * @param jwtToken - JWT authentication token
 * @param productId - Ops product UUID
 * @param networkId - Ops network UUID (string, not dbId)
 * @param environment - "UAT" or "Production"
 * @returns Deduplicated, sorted vehicle list with no warning on success
 */
export async function fetchOpsVehiclesForNetwork(
  jwtToken: string,
  productId: string,
  networkId: string,
  environment: Environment = 'UAT',
  productName?: string
): Promise<OpsVehicleLoadResult> {
  const isUuid = (value: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const seen = new Map<string, OpsVehicle>();
  let primaryRequestFailed = false;
  let primaryErrorMessage = '';
  const addVehicles = (items: OpsVehicle[]) => {
    for (const v of items) {
      if (v?.id && !seen.has(v.id)) {
        seen.set(v.id, {
          id: v.id,
          label: v.label ?? v.id,
          rego: v.rego,
          status: v.status,
          seatingCapacity: v.seatingCapacity,
        });
      }
    }
  };

  const formatSeatingCapacity = (seats?: FixedRouteVehicleListItem['seats']): string | undefined => {
    if (!Array.isArray(seats) || seats.length === 0) return undefined;
    const standard = seats.find((seat) => seat.capacityName === 'standard')?.amount;
    const wheelchair = seats.find((seat) => seat.capacityName === 'wheelchair')?.amount;
    if (typeof standard === 'number' || typeof wheelchair === 'number') {
      return `${standard ?? 0} std / ${wheelchair ?? 0} wc`;
    }
    const total = seats.reduce((sum, seat) => sum + (typeof seat.amount === 'number' ? seat.amount : 0), 0);
    return total > 0 ? `${total} seats` : undefined;
  };

  // Primary endpoint: fixed-route vehicle list.
  try {
    const response = await fetchFixedRouteVehicleList(jwtToken, productId, environment);
    const entries: FixedRouteVehicleListItem[] =
      response?.data?.entry ??
      (response?.data as any)?.entries ??
      response?.data?.vehicles ??
      response?.data?.items ??
      (response?.data as any)?.data?.entry ??
      (response?.data as any)?.data?.entries ??
      [];
    addVehicles(
      entries
        .filter((v) => String(v.status ?? '').toLowerCase() === 'operational')
        .map((v) => ({
          id: v.id,
          label: v.rego || v.name || v.label || v.id,
          rego: v.rego,
          status: v.status,
          seatingCapacity: formatSeatingCapacity(v.seats),
        }))
    );
  } catch (error) {
    primaryRequestFailed = true;
    primaryErrorMessage = error instanceof Error ? error.message : 'Unknown error';
  }

  // Fallback endpoint: Ops getvehicles by network UUID.
  // Only use fallback when the primary endpoint failed and we have a true UUID.
  if (seen.size === 0 && primaryRequestFailed && isUuid(networkId)) {
    const response = await fetchNetworkVehicleMaster(
      jwtToken,
      productId,
      networkId,
      environment,
      productName
    );
    const raw = response?.data?.vehicles ?? [];
    addVehicles(
      raw.map((v) => ({
        id: v.id,
        label: v.label ?? v.id,
      }))
    );
  }

  if (seen.size === 0 && primaryRequestFailed && !isUuid(networkId)) {
    throw new Error(
      `Failed to fetch fixed-route vehicles: ${primaryErrorMessage}. Ops fallback was skipped because network ID "${networkId}" is not a UUID.`
    );
  }

  const vehicles: OpsVehicle[] = Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));

  return {
    vehicles,
    warning:
      vehicles.length === 0
        ? 'No vehicles found for this network. Verify the network has registered vehicles in the Ops system.'
        : undefined,
  };
}
